import { Component, OnInit } from '@angular/core';
import { PagetoolbarComponent } from '../pagetoolbar/pagetoolbar.component';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatCardModule } from '@angular/material/card';
import { MatchChainComponent } from './match-chain/match-chain.component';
import { NotifyService } from '../../common/notify/notify.service';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatchingPeriodService } from '../../services/matchingPeriod.service';
import { MatchingPeriod } from '../../interfaces/matchingPeriod';
import { MatchRun } from '../../interfaces/matchRun';
import { MatchData } from '../../interfaces/matchData';
import { MatchRunService } from '../../services/match-run.service';
import { ParticipantService } from '../../services/participant.service';
import { Participant } from '../../interfaces/participant';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { SettingsComponent } from './settings/settings.component';
import { AuthService } from '../../auth/auth.service';
import { Permission } from '../../interfaces/permission';
import { MatchStartDialogComponent, MatchStartDialogResult } from '../match-run/match-start-dialog/match-start-dialog.component';
import { PairService } from '../../services/pair.service';
import { Pair } from '../../interfaces/pair';
import { MatchStartErrorDialogComponent } from '../match-run/match-start-error-dialog/match-start-error-dialog.component';

@Component({
  selector: 'app-match',
  imports: [PagetoolbarComponent, RouterLink, MatIconModule, MatMenuModule, MatCardModule, MatchChainComponent, CommonModule, MatButtonModule],
  providers: [NotifyService],
  templateUrl: './match.component.html',
  styleUrl: './match.component.scss'
})
export class MatchComponent implements OnInit {
  matchingPeriod!: MatchingPeriod;
  matchRun!: MatchRun;
  maxChains: number = 0; // Property to store the maximum number of chains
  groupedMatchData: { [chain: number]: MatchData[] } = {}; // Grouped match data by chain
  previousMatchRun?: MatchRun; // Previous match run
  nextMatchRun?: MatchRun; // Next match run
  participants!: Participant[];
  // OPTIMIZATION: Store full chain data with crossmatches and statuses
  chainsData: any[] = []; // Full chain data from backend including crossmatches and chain statuses
  canStartMatchrun = false;
  canAccessSettings = false;
  canAccessDownloads = false;
  hasNoMatches = false; // Property to track if no matches were found
  isCommentExpanded = true; // Track comment expansion state (default to expanded)
  canViewComments = false; // Permission to view comments
  isMatchRunInProgress = false; // Make read-only if a matchrun is in progress
  participantWarnings: {
    canStartMatchrun: boolean;
    message: string;
    participants: Array<{ enisNumber: string; type: 'donor' | 'recipient'; warnings: string[] }>;
  } | null = null;

  constructor(
    private readonly notify: NotifyService,
    private readonly route: ActivatedRoute,
    private readonly router: Router, // Inject Router
    private readonly matchingPeriodService: MatchingPeriodService,
    private readonly matchRunService: MatchRunService,
    private readonly participantService: ParticipantService,
    private readonly authService: AuthService,
    private dialog: MatDialog,
    private readonly pairService: PairService
  ) {}

  ngOnInit(): void {
    // Extract route parameters
    this.route.params.subscribe((params) => {
      const matchingPeriodName = params['matchperiodid']; // First part of the route
      const matchRunId = +params['matchrunid']; // Second part of the route (convert to number)

      // Check if user is HLA Lab and validate access
      this.authService.getUser().subscribe({
        next: (user) => {
          // Check if user can start matchruns
          this.canStartMatchrun = user.activeRole.permissions.includes(Permission.MATCHRUN_START);
          // Check if user can access settings
          this.canAccessSettings = user.activeRole.permissions.includes(Permission.MATCHRUN_SETTINGS);
          this.canAccessDownloads = user.activeRole.permissions.includes(Permission.MATCHRUN_DOWNLOAD);
          this.canViewComments = user.activeRole.permissions.includes(Permission.MATCHRUN_VIEW_COMMENTS);
          if (user.activeRole.name === 'HLA Lab') {
            // For HLA Lab users, check if they're trying to access the latest matching period
            this.matchingPeriodService.getLatestMatchingPeriodForHLALab().subscribe({
              next: (latestPeriod) => {
                if (latestPeriod && latestPeriod.matchingPeriodName !== matchingPeriodName) {
                  // Redirect to latest matching period if trying to access an older one
                  this.notify.withAutodismiss('HLA Lab users can only view data from the latest matching period');
                  this.router.navigate(['/matchrun', latestPeriod.matchingPeriodName, matchRunId]);
                  return;
                }
                // Load the data normally if this IS the latest period
                this.loadMatchingPeriodData(matchingPeriodName, matchRunId);
              },
              error: (error) => {
                console.error('Error checking HLA Lab access:', error);
                // If there's an error getting the latest period for HLA Lab, try loading normally
                // This could happen if there's no matching period yet
                this.notify.withAutodismiss('No matching period data available for HLA Lab users');
              }
            });
          } else {
            // For non-HLA Lab users, load data normally
            this.loadMatchingPeriodData(matchingPeriodName, matchRunId);
          }
        },
        error: (error) => {
          console.error('Error getting user role:', error);
          // If we can't get the user role, try loading the data normally
          this.loadMatchingPeriodData(matchingPeriodName, matchRunId);
        }
      });
    });

    // Check if a matchrun is currently in progress
    this.matchRunService.getMatchRunStatus().subscribe({
      next: (status) => {
        this.isMatchRunInProgress = status === 'IN_PROGRESS';
      },
      error: (err) => {
        console.log('Error fetching matchrun status:', err);
      }
    });

    // Check for participant warnings
    this.checkForParticipantWarnings();
  }

  private loadMatchingPeriodData(matchingPeriodName: string, matchRunId: number): void {
    // OPTIMIZATION: Fetch matching period for metadata (navigation, header info)
    // We still need this for matchingPeriod details and navigation between matchruns
    this.matchingPeriodService.getMatchingPeriod(matchingPeriodName).subscribe((currentMatchingPeriodResult) => {
      // Assign the result to matchingPeriod
      this.matchingPeriod = currentMatchingPeriodResult;

      // Find the matchRun where runNumber matches matchRunId
      this.matchRun = this.matchingPeriod?.matchRuns?.find((run) => run.runNumber === matchRunId)!;

      // OPTIMIZATION FIX: Initialize matchData as empty array immediately to prevent template errors
      // This will be populated when getMatchRunChains() completes
      if (this.matchRun) {
        this.matchRun.matchData = [];
      }

      console.log(this.matchingPeriod);
      // Determine previous and next match runs
      this.setMatchRunNavigation();

      // OPTIMIZATION: Use new pre-assembled chains endpoint instead of separate API calls
      // This replaces: getMatchingPeriod() + getMatchRunParticipants() + N×(getCrossmatches + getChainStatus)
      // With a single API call that returns everything pre-assembled
      this.matchingPeriodService.getMatchRunChains(matchingPeriodName, matchRunId).subscribe((chains) => {
        // Check if there are any matches
        this.hasNoMatches = !chains || chains.length === 0;

        if (this.hasNoMatches) {
          this.maxChains = 0;
          this.groupedMatchData = {};
          this.participants = [];
          this.chainsData = [];
          // OPTIMIZATION FIX: Initialize empty matchData array when no matches
          if (this.matchRun) {
            this.matchRun.matchData = [];
          }
        } else {
          // Store full chains data for passing to child components
          this.chainsData = chains;

          // Calculate the maximum number of chains
          this.maxChains = chains.length;

          // Transform chains to grouped matchData format for backward compatibility
          this.groupedMatchData = {};
          const participantMap = new Map<string, Participant>();

          chains.forEach((chain: any) => {
            // Store grouped matchData
            this.groupedMatchData[chain.chainNumber] = chain.matchData;

            // Collect unique participants
            chain.matchData.forEach((md: any) => {
              if (md.participant && !participantMap.has(md.participant.enisNumber)) {
                participantMap.set(md.participant.enisNumber, md.participant);
              }
            });
          });

          // Convert participant map to array
          this.participants = Array.from(participantMap.values());

          // OPTIMIZATION FIX: Populate matchRun.matchData from chains response
          // This is needed because the template expects matchRun.matchData to exist
          // but we optimized the backend to not load it in getMatchingPeriod()
          if (this.matchRun) {
            this.matchRun.matchData = chains.flatMap((chain: any) => chain.matchData);
          }
        }
      });
    });
  }
  private setMatchRunNavigation(): void {
    const matchRuns = this.matchingPeriod.matchRuns;
    if (matchRuns) {
      const currentIndex = matchRuns.findIndex((run) => run.runNumber === this.matchRun.runNumber);
      this.previousMatchRun = currentIndex > 0 ? matchRuns[currentIndex - 1] : undefined;
      this.nextMatchRun = currentIndex < matchRuns.length - 1 ? matchRuns[currentIndex + 1] : undefined;
    }
  }
  navigateTo(runNumber: number): void {
    const matchingPeriodName = this.matchingPeriod.matchingPeriodName;
    this.router.navigate([`/matchrun/${matchingPeriodName}/${runNumber}`]);
    this.setMatchRunNavigation();
  }

  // Method to group matchData by chain
  private groupByChain(matchData: MatchData[]): { [chain: number]: MatchData[] } {
    return matchData.reduce(
      (groups, item) => {
        const chain = item.chain;
        if (!groups[chain]) {
          groups[chain] = [];
        }
        groups[chain].push(item);
        return groups;
      },
      {} as { [chain: number]: MatchData[] }
    );
  }

  // Helper method to get full chain data by chain number
  getChainData(chainNumber: string | number): any {
    const chainNum = typeof chainNumber === 'string' ? parseInt(chainNumber) : chainNumber;
    return this.chainsData.find(chain => chain.chainNumber === chainNum);
  }

  // BUG FIX: Custom comparator for keyvalue pipe to sort chain numbers numerically
  // Without this, chains are sorted lexicographically: "1", "10", "11", "2", "3"...
  // With this, chains are sorted numerically: 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11...
  compareChainNumbers = (a: any, b: any): number => {
    const chainA = parseInt(a.key);
    const chainB = parseInt(b.key);
    return chainA - chainB;
  };

  getExcelFiles() {
    this.matchRunService.getMatchrunExcelfiles(this.matchRun.dateCreated.toString()).subscribe({
      next: (signedUrls) => {
        signedUrls.forEach((url) => {
          const link = document.createElement('a');
          link.href = url;
          link.download = ''; // Optional: Set a default filename if the backend doesn't provide one
          link.target = '_blank'; // Open in a new tab if needed
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        });
      },
      error: (err) => {
        console.error('Error fetching signed URLs:', err);
      }
    });
  }

  getSettings() {
    // Fetch JSON data from the backend
    this.matchRunService.getSettings().subscribe((settings: any) => {
      // Open the dialog with the fetched settings
      const dialogRef = this.dialog.open(SettingsComponent, {
        width: '600px',
        data: settings
      });

      // Handle the dialog close event
      dialogRef.afterClosed().subscribe((updatedSettings) => {
        if (updatedSettings) {
          // Send the updated settings back to the backend
          this.matchRunService.setSettings(updatedSettings).subscribe(() => {
            console.log('Settings updated successfully');
          });
        }
      });
    });
  }

  // Helper method to convert Unix timestamp to proper date
  getFormattedDate(timestamp: number): string {
    if (!timestamp) return 'Unknown';

    // Convert from seconds to milliseconds if needed
    const dateInMs = timestamp > 1000000000000 ? timestamp : timestamp * 1000;
    const date = new Date(dateInMs);

    return date.toLocaleString();
  }

  // Helper method to parse algorithm errors from JSON string
  getAlgorithmErrors(): string[] {
    if (!this.matchRun || !this.matchRun.algorithmErrors) {
      return [];
    }

    try {
      const errors = JSON.parse(this.matchRun.algorithmErrors);
      return Array.isArray(errors) ? errors : [];
    } catch (error) {
      console.error('Error parsing algorithm errors:', error);
      return [this.matchRun.algorithmErrors]; // Fallback to treating as single error message
    }
  }

  // Removed getPreviousMatchRunChains() - no longer needed since chain appearance isn't a meaningful change

  // Toggle comment expansion
  toggleCommentExpanded(): void {
    this.isCommentExpanded = !this.isCommentExpanded;
  }

  // Start new matchrun from this view
  startNewMatchrun(): void {
    // First, fetch pairs statistics
    this.pairService.getAllPairs().subscribe({
      next: (pairs: Pair[]) => {
        // Calculate statistics
        const totalPairs = pairs.length;
        const activePairs = pairs.filter((pair) => pair.active).length;
        const activeConfirmedPairs = pairs.filter((pair) => pair.active).length;
        const inactivePairs = pairs.filter((pair) => !pair.active).length;

        // Get participants that need ENIS sync
        this.participantService.getAllParticipants().subscribe({
          next: (participants: Participant[]) => {
            const enisSyncNeeded = participants.filter(
              (participant) => !participant.firstName && !participant.lastName
            ).length;

            // Open dialog with statistics
            const dialogRef = this.dialog.open(MatchStartDialogComponent, {
              width: '600px',
              disableClose: true,
              data: {
                currentMatchRunId: this.matchRun.id,
                pairsStatistics: {
                  totalPairs,
                  activeConfirmedPairs,
                  enisSyncNeeded,
                  activePairs,
                  inactivePairs
                },
                participantWarnings: this.participantWarnings
              }
            });

            dialogRef.afterClosed().subscribe((result: MatchStartDialogResult) => {
              if (result?.confirmed) {
                // Start the matchrun with the comment for the current matchrun
                this.matchRunService.startMatchrun(result.comment, this.matchRun.id, result.isMajorMatchrun).subscribe({
                  next: () => {
                    // Navigate to the match start page to show progress
                    // Pass state to indicate matchrun just started so loading screen shows immediately
                    this.router.navigate(['/matchstart'], {
                      state: { matchrunStarted: true }
                    });
                  },
                  error: (err) => {
                    console.error('Error starting matchrun:', err);
                    const errorMessage = err.message || 'Failed to start matchrun. Please try again.';

                    // Check if this is a partial lock validation error
                    if (errorMessage.includes('partially locked chains')) {
                      // Show detailed error dialog for partial lock errors
                      this.dialog.open(MatchStartErrorDialogComponent, {
                        width: '600px',
                        data: {
                          title: 'Cannot Start Matchrun',
                          message: errorMessage,
                          isPartialLockError: true
                        }
                      });
                    } else {
                      // Show generic error notification
                      this.notify.withAutodismiss(errorMessage);
                    }
                  }
                });
              }
            });
          },
          error: (err) => {
            console.error('Error fetching participants:', err);
            // Open dialog without ENIS sync count if there's an error
            this.openDialogWithoutFullStats(totalPairs, activePairs, activeConfirmedPairs, inactivePairs);
          }
        });
      },
      error: (err) => {
        console.error('Error fetching pairs:', err);
        // Open dialog without statistics if there's an error
        this.openDialogWithoutStats();
      }
    });
  }

  // Helper method to open dialog without full statistics
  private openDialogWithoutFullStats(
    totalPairs: number,
    activePairs: number,
    activeConfirmedPairs: number,
    inactivePairs: number
  ): void {
    const dialogRef = this.dialog.open(MatchStartDialogComponent, {
      width: '600px',
      disableClose: true,
      data: {
        currentMatchRunId: this.matchRun.id,
        pairsStatistics: {
          totalPairs,
          activeConfirmedPairs,
          enisSyncNeeded: 0,
          activePairs,
          inactivePairs
        },
        participantWarnings: this.participantWarnings
      }
    });

    dialogRef.afterClosed().subscribe((result: MatchStartDialogResult) => {
      if (result?.confirmed) {
        this.matchRunService.startMatchrun(result.comment, this.matchRun.id, result.isMajorMatchrun).subscribe({
          next: () => {
            this.router.navigate(['/matchstart'], {
              state: { matchrunStarted: true }
            });
          },
          error: (err) => {
            console.error('Error starting matchrun:', err);
            const errorMessage = err.message || 'Failed to start matchrun. Please try again.';

            // Check if this is a partial lock validation error
            if (errorMessage.includes('partially locked chains')) {
              // Show detailed error dialog for partial lock errors
              this.dialog.open(MatchStartErrorDialogComponent, {
                width: '600px',
                data: {
                  title: 'Cannot Start Matchrun',
                  message: errorMessage,
                  isPartialLockError: true
                }
              });
            } else {
              // Show generic error notification
              this.notify.withAutodismiss(errorMessage);
            }
          }
        });
      }
    });
  }

  checkForParticipantWarnings() {
    this.matchRunService.checkParticipantWarnings().subscribe({
      next: (result) => {
        this.participantWarnings = result;
        console.log('Participant warnings check:', result);
      },
      error: (error) => {
        console.error('Error checking participant warnings:', error);
        // On error, allow matchrun to proceed (fail open)
        this.participantWarnings = null;
      }
    });
  }

  // Helper method to open dialog without statistics
  private openDialogWithoutStats(): void {
    const dialogRef = this.dialog.open(MatchStartDialogComponent, {
      width: '600px',
      disableClose: true,
      data: {
        currentMatchRunId: this.matchRun.id,
        participantWarnings: this.participantWarnings
      }
    });

    dialogRef.afterClosed().subscribe((result: MatchStartDialogResult) => {
      if (result?.confirmed) {
        this.matchRunService.startMatchrun(result.comment, this.matchRun.id, result.isMajorMatchrun).subscribe({
          next: () => {
            this.router.navigate(['/matchstart'], {
              state: { matchrunStarted: true }
            });
          },
          error: (err) => {
            console.error('Error starting matchrun:', err);
            const errorMessage = err.message || 'Failed to start matchrun. Please try again.';

            // Check if this is a partial lock validation error
            if (errorMessage.includes('partially locked chains')) {
              // Show detailed error dialog for partial lock errors
              this.dialog.open(MatchStartErrorDialogComponent, {
                width: '600px',
                data: {
                  title: 'Cannot Start Matchrun',
                  message: errorMessage,
                  isPartialLockError: true
                }
              });
            } else {
              // Show generic error notification
              this.notify.withAutodismiss(errorMessage);
            }
          }
        });
      }
    });
  }
}
